/* eslint-disable no-nested-ternary */
import Skeleton from '@mui/material/Skeleton';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAllMatchStatsWC26,
  useBracketFixtures,
  useBracketStandings,
  useDirectFkGoals,
  useGoalSeconds,
  useGroupWinnersWC26,
  useKeeperGoals,
  useOlimpicoGoals,
  useTopScorersWC26,
  useWC26ScoringRules,
  useWC26UserTeams,
} from '../../queries';
import { Goal, Scores } from '../../types';
import {
  calculateResults, calculateScores, getMostRedsWinnerIds, goalLatestSortKey, goalTotalSeconds,
} from '../../utils/scoringUtils';
import {
  BonusesTooltip, ScoreTooltip, Tooltip, TotalTooltip,
} from './ScoreTooltip';
import './ScoreboardWC26.css';

function getRowClass(index: number, totalRows: number): string {
  if (index === 0) return 'gold';
  if (index === 1) return 'silver';
  if (index === 2) return 'bronze';
  if (index === totalRows - 1) return 'wooden-spoon';
  return '';
}

function getPrize(index: number, totalRows: number): string | null {
  if (index === 0) return '€300';
  if (index === 1) return '€100';
  if (index === 2) return '€60';
  if (index === totalRows - 1) return '€20';
  return null;
}

function totUpScore(score: Scores): number {
  return score.score
    + score.groupWinner
    + score.earliestGoal
    + score.latestGoal
    + score.goldenBoot
    + score.hattrick
    + score.directFk
    + score.olimpico
    + score.keeperGoal
    + score.mostReds
    + score.teamGoals3plus;
}

function getPosition(index: number, scores: Scores[]): string {
  if (index > 0 && totUpScore(scores[index]) === totUpScore(scores[index - 1])) return '=';
  return `${index + 1}`;
}

function ScoreboardWC26() {
  const navigate = useNavigate();
  const [userTeamsLoading, userTeams, userTeamsError] = useWC26UserTeams();
  const [matchStatsLoading, matchStats, matchStatsError] = useAllMatchStatsWC26();
  const [rulesLoading, rules, rulesError] = useWC26ScoringRules();
  const [topScorersLoading, topScorers, topScorersError] = useTopScorersWC26();
  const [groupWinnersLoading, groupWinners, groupWinnersError] = useGroupWinnersWC26();
  const [, directFkGoals] = useDirectFkGoals();
  const [, olimpicoGoals] = useOlimpicoGoals();
  const [, keeperGoals] = useKeeperGoals();
  const [, goalSecondsData] = useGoalSeconds();
  const [, standings] = useBracketStandings();
  const [, bracketFixtures] = useBracketFixtures();

  const logoById = (standings ?? []).reduce<Record<string, string>>((acc, s) => {
    acc[s.team.id] = s.team.logo;
    return acc;
  }, {});

  // Build set of eliminated team IDs
  const eliminatedIds = new Set<string>();
  // Group stage: rank 4 after 3 games always out; rank 3 after 3 games out unless best 3rd
  const thirdPlace = (standings ?? []).filter((s) => s.rank === 3 && s.group !== 'Group Stage');
  const sorted3rd = [...thirdPlace].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.goalsDiff !== a.goalsDiff) return b.goalsDiff - a.goalsDiff;
    return b.goalsFor - a.goalsFor;
  });
  const qualifying3rdIds = new Set(sorted3rd.slice(0, 8).map((s) => s.team.id));
  (standings ?? []).forEach((s) => {
    if (s.all.played < 3) return;
    if (s.rank === 4) eliminatedIds.add(s.team.id.toString());
    if (s.rank === 3 && !qualifying3rdIds.has(s.team.id)) eliminatedIds.add(s.team.id.toString());
  });
  // Knockout stage: loser of a finished match is eliminated
  const FINISHED = ['FT', 'AET', 'PEN'];
  (bracketFixtures ?? [])
    .filter((f) => !f.round.startsWith('Group Stage') && FINISHED.includes(f.status.short))
    .forEach((f) => {
      const hg = f.goals.home ?? 0;
      const ag = f.goals.away ?? 0;
      // For PEN, use matchStatsById homeWinner/awayWinner if available
      const stat = (matchStats ?? []).find((m) => m.id === f.id);
      let loserSide: 'home' | 'away' | null = null;
      if (f.status.short === 'PEN' && stat) {
        if (stat.homeWinner) loserSide = 'away';
        else if (stat.awayWinner) loserSide = 'home';
      } else if (hg !== ag) {
        loserSide = hg > ag ? 'away' : 'home';
      }
      if (loserSide) eliminatedIds.add(f[loserSide].id.toString());
    });
  const [scores, setScores] = useState<Scores[]>([]);

  const loading = userTeamsLoading || matchStatsLoading || rulesLoading
    || topScorersLoading || groupWinnersLoading;
  const error = userTeamsError || matchStatsError || rulesError
    || topScorersError || groupWinnersError;

  useEffect(() => {
    if (loading || !userTeams || !rules) return;

    const stats = matchStats ?? [];
    const secondsMap = goalSecondsData ?? {};
    const goalsWithMatch = stats.reduce<{ goal: Goal; matchId: number }[]>(
      (acc, m) => [...acc, ...m.goalEvents
        .filter((g) => g.type !== 'Own Goal' && g.type !== 'Missed Penalty')
        .map((g) => ({ goal: g, matchId: m.id }))],
      []
    );
    const earliestGoal = [...goalsWithMatch].sort(
      (a, b) => goalTotalSeconds(a.matchId, a.goal, secondsMap)
        - goalTotalSeconds(b.matchId, b.goal, secondsMap)
    )[0]?.goal;
    const latestGoal = [...goalsWithMatch].sort(
      (a, b) => goalLatestSortKey(b.goal) - goalLatestSortKey(a.goal)
    )[0]?.goal;

    const teamResults = calculateResults(userTeams, stats);
    const mostRedsWinnerIds = getMostRedsWinnerIds(teamResults, userTeams);
    const calculatedScores = calculateScores(
      teamResults,
      userTeams,
      rules,
      topScorers?.[0]?.teamId,
      (groupWinners ?? []).filter((w) => w.group !== 'Group Stage'),
      earliestGoal,
      latestGoal,
      stats,
      directFkGoals ?? [],
      olimpicoGoals ?? [],
      keeperGoals ?? [],
      mostRedsWinnerIds
    );
    calculatedScores.sort((a, b) => totUpScore(b) - totUpScore(a));
    setScores(calculatedScores);
  }, [
    userTeams,
    matchStats,
    rules,
    topScorers,
    groupWinners,
    directFkGoals,
    olimpicoGoals,
    keeperGoals,
    goalSecondsData
  ]);

  return (
    <div className="scoreboard-wc26">
      <h2 className="scoreboard-wc26__title">Scoreboard</h2>
      <p className="scoreboard-wc26-info">Bonuses: Group Winner, Golden Boot, Goals &amp; Specials</p>
      <p className="scoreboard-wc26-info">Sorted by Total</p>
      {error ? <p className="scoreboard-wc26-info">Failed to load scoreboard.</p>
        : loading && scores.length === 0 ? (
          <table className="scoreboard-wc26__table">
            <thead>
              <tr className="scoreboard-wc26__header-row">
                {['#', 'Player', 'Teams', 'Score', 'Bonuses', 'Total'].map((h) => (
                  <th key={h} className="scoreboard-wc26__header-cell">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i} className="scoreboard-wc26__body-row">
                  {Array.from({ length: 6 }).map((__, j) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={j} className="scoreboard-wc26__body-cell">
                      <Skeleton variant="text" width="80%" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )
          : scores.length === 0
            ? <p className="scoreboard-wc26-info">No scores yet</p>
            : (
              <table className="scoreboard-wc26__table">
                <thead>
                  <tr className="scoreboard-wc26__header-row">
                    <th className="scoreboard-wc26__header-cell">#</th>
                    <th className="scoreboard-wc26__header-cell">Player</th>
                    <th className="scoreboard-wc26__header-cell">Teams</th>
                    <th className="scoreboard-wc26__header-cell">Score</th>
                    <th className="scoreboard-wc26__header-cell">Bonuses</th>
                    <th className="scoreboard-wc26__header-cell">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {scores.map((score, index) => (
                    <tr
                      key={score.name}
                      className={`scoreboard-wc26__body-row ${getRowClass(index, scores.length)}`}
                    >
                      <td className="scoreboard-wc26__body-cell scoreboard-wc26__rank-cell">
                        {getPosition(index, scores)}
                      </td>
                      <td className="scoreboard-wc26__body-cell">
                        <button
                          type="button"
                          className="scoreboard-wc26__name-cell"
                          onClick={() => navigate(`/wc26-user/${encodeURIComponent(score.name)}`)}
                        >
                          {['🥇', '🥈', '🥉'][index] ?? (index === scores.length - 1 ? '🥄' : '')}
                          {' '}
                          {score.name}
                        </button>
                        {getPrize(index, scores.length) && (
                          <span className="scoreboard-wc26__prize">{getPrize(index, scores.length)}</span>
                        )}
                      </td>
                      <td className="scoreboard-wc26__body-cell scoreboard-wc26__flags-cell">
                        {(() => {
                          const ut = userTeams?.find((u) => u.name === score.name);
                          if (!ut) return null;
                          const teams = [{ name: ut.team1, id: ut.team1Id },
                            { name: ut.team2, id: ut.team2Id }];
                          return (
                            <Tooltip
                              direction="down"
                              content={(
                                <div className="scoreboard-wc26__teams-tooltip">
                                  {teams.map((t) => (
                                    <div key={t.id} className="scoreboard-wc26__teams-tooltip-row">
                                      {logoById[t.id] && <img src={logoById[t.id]} alt={t.name} className={`scoreboard-wc26__teams-tooltip-flag${eliminatedIds.has(t.id) ? ' eliminated' : ''}`} />}
                                      <span className={eliminatedIds.has(t.id) ? 'scoreboard-wc26__teams-tooltip-eliminated' : ''}>{t.name}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            >
                              <div className="scoreboard-wc26__flags-stack">
                                {teams.map((t) => logoById[t.id] && (
                                  <img key={t.id} src={logoById[t.id]} alt={t.name} className={`scoreboard-wc26__flags-stack-img${eliminatedIds.has(t.id) ? ' eliminated' : ''}`} />
                                ))}
                              </div>
                            </Tooltip>
                          );
                        })()}
                      </td>
                      <td className="scoreboard-wc26__body-cell"><ScoreTooltip score={score} direction="up" /></td>
                      <td className="scoreboard-wc26__body-cell"><BonusesTooltip score={score} direction="up" /></td>
                      <td className="scoreboard-wc26__body-cell"><TotalTooltip score={score} direction="up" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
    </div>
  );
}

export default ScoreboardWC26;
